import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setBearer, setRefreshToken, getRefreshToken } from "@/lib/api";
import { clearReadCache } from "@/lib/markupsApi";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, object = authed, false = not authed
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // 2026-09-14 (ForgedOps completion) — see the matching comment in
  // `lib/api.js::clearSession()`. Without this, a dead session (401
  // with no valid/working refresh token) never propagated back into
  // React state, so `ProtectedRoute` never redirected and every
  // background poller on the current page kept hammering the dead
  // session forever. This makes the interceptor's "the session is
  // really over" decision immediately visible to the whole app.
  useEffect(() => {
    const onExpired = () => setUser(false);
    window.addEventListener("fop:session-expired", onExpired);
    return () => window.removeEventListener("fop:session-expired", onExpired);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Hard-Gate P1 (2026-09-06) — an enrolled account gets a short-lived
    // mfa_token + mfa_required:true instead of access_token/user. Surface
    // that distinctly so the caller (Login.jsx) can show a code-entry step
    // instead of silently "succeeding" with no token and no user.
    if (data.mfa_required) {
      return { mfaRequired: true, mfaToken: data.mfa_token };
    }
    if (data.access_token) setBearer(data.access_token);
    if (data.refresh_token) setRefreshToken(data.refresh_token);
    setUser(data.user);
    return data.user;
  };

  const verifyMfa = async (mfaToken, code) => {
    const { data } = await api.post("/auth/mfa/verify", { mfa_token: mfaToken, code });
    if (data.access_token) setBearer(data.access_token);
    if (data.refresh_token) setRefreshToken(data.refresh_token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.access_token) setBearer(data.access_token);
    if (data.refresh_token) setRefreshToken(data.refresh_token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      // FP-006 (2026-09-09 audit remediation) — pass along the refresh
      // token this session actually holds so the backend can revoke it
      // by exact jti, in addition to its own Bearer-derived revoke-all.
      const rt = getRefreshToken();
      await api.post("/auth/logout", null, rt ? { headers: { "X-Refresh-Token": rt } } : undefined);
    } catch {}
    setBearer(null);
    setRefreshToken(null);
    setUser(false);
    // R-06 (independent recheck, 2026-10-01) — the in-memory
    // last-known-good read cache in markupsApi.js lives at the JS
    // module level, outside the Service Worker cache purge below; it
    // survives a logout within the same tab and could otherwise serve
    // the departing identity's cached markups/objects to whoever logs
    // in next on this device.
    clearReadCache();
    // Wave B · Turn 1 · WP-B1.04 QA follow-up (iteration_182) — the SW now
    // caches authenticated GETs (including /auth/me and per-sheet data)
    // keyed by URL only, with no per-user partitioning. On a shared
    // device, the next person to sign in and go offline could otherwise
    // be served the previous user's cached identity/project data. Purge
    // every fop-sw-* cache on logout so a fresh session starts clean.
    if ("caches" in window) {
      try {
        const keys = await caches.keys();
        await Promise.all(keys.filter((k) => k.startsWith("fop-sw-")).map((k) => caches.delete(k)));
      } catch {}
    }
  };

  const acceptInvite = async (token, password) => {
    const { data } = await api.post(`/tenant/users/invitations/${token}/accept`, { token, password });
    if (data.access_token) setBearer(data.access_token);
    if (data.refresh_token) setRefreshToken(data.refresh_token);
    if (data.user) setUser(data.user);
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, verifyMfa, register, logout, acceptInvite, refresh: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
